import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { narApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const P   = '#6B3FA0';
const PD  = '#4A2B70';
const PL  = '#F3EEFA';
const PLM = '#E5D5F5';

const ANSWER_META = {
  met:           { label: 'Met',           color: '#16a34a', bg: '#D1FAE5', border: '#A7F3D0' },
  partially_met: { label: 'Partially Met', color: '#ca8a04', bg: '#FEF3C7', border: '#FDE68A' },
  not_met:       { label: 'Not Met',       color: '#dc2626', bg: '#FEE2E2', border: '#FECACA' },
  na:            { label: 'N/A',           color: '#6B7280', bg: '#F3F4F6', border: '#E5E7EB' },
};

export default function NarConduct() {
  const { narId } = useParams();
  const navigate  = useNavigate();
  const { user }  = useAuth();

  const [nar, setNar]               = useState(null);
  const [sections, setSections]     = useState([]);
  const [answers, setAnswers]       = useState({});
  const [activeSec, setActiveSec]   = useState(0);
  const [saving, setSaving]         = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading]       = useState(true);

  // Filter state
  const [filterUnanswered, setFilterUnanswered] = useState(false);
  const [filterRemote, setFilterRemote]         = useState(false);
  const [filterTags, setFilterTags]             = useState(new Set());
  const [filterStatuses, setFilterStatuses]     = useState(new Set());

  // Import state
  const [importingAnswers, setImportingAnswers] = useState(false);
  const importRef = useRef();
  const pendingRef = useRef({});

  // ── load ────────────────────────────────────────────────────────────────────
  const loadAnswers = useCallback(async () => {
    const answersData = await narApi.getAnswers(narId);
    const map = {};
    for (const a of (answersData.data.answers || [])) {
      map[a.question_id] = {
        answer: a.answer,
        comments: a.comments || '',
        internal_notes: a.internal_notes || '',
        vcio_comments: a.vcio_comments || '',
      };
    }
    return map;
  }, [narId]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const narData = (await narApi.getNar(narId)).data.nar;
        const [structData, answerMap] = await Promise.all([
          narApi.getTypeStructure(narData.nar_type_id),
          loadAnswers(),
        ]);
        setNar(narData);
        setSections(structData.data.sections || []);
        setAnswers(answerMap);
      } catch (e) {
        toast.error('Failed to load NAR');
        navigate('/nar/assessments');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [narId]);

  // ── save ────────────────────────────────────────────────────────────────────
  const save = useCallback(async () => {
    const pending = { ...pendingRef.current };
    pendingRef.current = {};
    const items = Object.entries(pending).map(([qid, data]) => ({ question_id: Number(qid), ...data }));
    if (!items.length) return;
    setSaving(true);
    try {
      await narApi.saveAnswers(narId, items);
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  }, [narId]);

  useEffect(() => {
    const timer = setInterval(() => save(), 30000);
    return () => clearInterval(timer);
  }, [save]);

  const handleAnswer = useCallback((qid, field, value) => {
    setAnswers(prev => {
      const updated = { ...prev, [qid]: { ...(prev[qid] || {}), [field]: value } };
      pendingRef.current[qid] = updated[qid];
      return updated;
    });
  }, []);

  const handleSaveNow = async () => {
    await save();
    toast.success('Progress saved');
  };

  const handleSubmit = async () => {
    if (!window.confirm('Submit this NAR? All questions must be answered.')) return;
    await save();
    setSubmitting(true);
    try {
      await narApi.submitNar(narId);
      toast.success('NAR submitted!');
      navigate(`/nar/assessments/${narId}/report`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Excel export ─────────────────────────────────────────────────────────────
  const handleExportAnswers = async () => {
    try {
      await narApi.exportAnswers(narId, nar?.name);
    } catch (e) {
      toast.error('Export failed');
    }
  };

  // ── Excel import ─────────────────────────────────────────────────────────────
  const handleImportAnswers = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportingAnswers(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await narApi.importAnswers(narId, fd);
      toast.success(`Imported ${res.data.imported} answers${res.data.skipped ? ` (${res.data.skipped} skipped)` : ''}`);
      const map = await loadAnswers();
      setAnswers(map);
      pendingRef.current = {};
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
    } finally {
      setImportingAnswers(false);
      e.target.value = '';
    }
  };

  // ── derived stats ────────────────────────────────────────────────────────────
  const allQuestions = sections.flatMap(s =>
    (s.categories || []).flatMap(c => c.questions || [])
  );
  const totalQ    = allQuestions.length;
  const answeredQ = allQuestions.filter(q => answers[q.id]?.answer).length;
  const pct       = totalQ > 0 ? Math.round(answeredQ / totalQ * 100) : 0;

  const sectionProgress = (sec) => {
    const qs = (sec.categories || []).flatMap(c => c.questions || []);
    const ans = qs.filter(q => answers[q.id]?.answer).length;
    return { total: qs.length, answered: ans };
  };

  // ── filter logic ─────────────────────────────────────────────────────────────
  const allTags = useMemo(() => {
    const map = {};
    for (const sec of sections) {
      for (const cat of sec.categories || []) {
        for (const q of cat.questions || []) {
          for (const t of q.tags || []) {
            map[t.id] = t;
          }
        }
      }
    }
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }, [sections]);

  const anyFilter = filterUnanswered || filterRemote || filterTags.size > 0 || filterStatuses.size > 0;

  const matchesFilter = useCallback((q) => {
    if (filterUnanswered && answers[q.id]?.answer) return false;
    if (filterRemote && !q.answerable_remotely) return false;
    if (filterTags.size > 0) {
      const qTagIds = (q.tags || []).map(t => t.id);
      if (!qTagIds.some(id => filterTags.has(id))) return false;
    }
    if (filterStatuses.size > 0) {
      const ans = answers[q.id]?.answer || null;
      if (!filterStatuses.has(ans)) return false;
    }
    return true;
  }, [filterUnanswered, filterRemote, filterTags, filterStatuses, answers]);

  const toggleTag = (tid) => setFilterTags(prev => {
    const next = new Set(prev);
    if (next.has(tid)) next.delete(tid); else next.add(tid);
    return next;
  });

  const toggleStatus = (status) => setFilterStatuses(prev => {
    const next = new Set(prev);
    if (next.has(status)) next.delete(status); else next.add(status);
    return next;
  });

  const clearFilters = () => {
    setFilterUnanswered(false);
    setFilterRemote(false);
    setFilterTags(new Set());
    setFilterStatuses(new Set());
  };

  // sections rendered in the question panel
  const viewSections = useMemo(() => {
    if (!anyFilter) return sections[activeSec] ? [sections[activeSec]] : [];
    return sections.map(sec => ({
      ...sec,
      categories: (sec.categories || []).map(cat => ({
        ...cat,
        questions: (cat.questions || []).filter(matchesFilter),
      })).filter(cat => cat.questions.length > 0),
    })).filter(sec => sec.categories.length > 0);
  }, [anyFilter, sections, activeSec, matchesFilter]);

  const isAdmin  = user?.role === 'admin';
  const isLocked = nar?.status === 'submitted';

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '80px', color: '#6B7280' }}>Loading…</div>;
  }

  const btnStyle = (bg, color, border) => ({
    padding: '8px 14px', backgroundColor: bg, color, border: border || 'none',
    borderRadius: '6px', fontWeight: '600', fontSize: '13px', cursor: 'pointer',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Top bar */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '14px 20px', marginBottom: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: PD }}>{nar?.name}</h1>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6B7280' }}>
            {nar?.client_name} · {nar?.nar_type_name}
            {isLocked && <span style={{ marginLeft: '10px', padding: '2px 8px', backgroundColor: '#D1FAE5', color: '#065F46', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>Submitted</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Progress count */}
          <div style={{ textAlign: 'right', minWidth: '80px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: PD }}>{answeredQ} / {totalQ}</div>
            <div style={{ fontSize: '11px', color: '#6B7280' }}>answered</div>
          </div>
          <div style={{ width: '90px', height: '7px', backgroundColor: PLM, borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', backgroundColor: pct === 100 ? '#16a34a' : P, borderRadius: '4px', transition: 'width 0.3s ease' }} />
          </div>

          {/* Excel / Import */}
          <button onClick={handleExportAnswers} style={btnStyle('white', '#374151', '1px solid #D1D5DB')} title="Export answers to Excel">
            ⬇ Excel
          </button>
          {!isLocked && (
            <>
              <button onClick={() => importRef.current?.click()} disabled={importingAnswers}
                style={{ ...btnStyle('#D1FAE5', '#065F46', '1px solid #A7F3D0'), opacity: importingAnswers ? 0.7 : 1 }}>
                {importingAnswers ? 'Importing…' : '⬆ Import'}
              </button>
              <input ref={importRef} type="file" accept=".xlsx" onChange={handleImportAnswers} style={{ display: 'none' }} />
            </>
          )}

          {/* Save / Submit / Reopen */}
          {!isLocked && (
            <>
              <button onClick={handleSaveNow} disabled={saving}
                style={btnStyle('white', PD, `1px solid ${PLM}`)}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={handleSubmit} disabled={submitting || pct < 100}
                title={pct < 100 ? `${totalQ - answeredQ} questions still unanswered` : 'Submit NAR'}
                style={{ ...btnStyle(pct === 100 ? '#16a34a' : '#D1D5DB', 'white'), cursor: pct === 100 ? 'pointer' : 'not-allowed' }}>
                {submitting ? 'Submitting…' : 'Submit NAR'}
              </button>
            </>
          )}
          {isLocked && isAdmin && (
            <button onClick={async () => { await narApi.reopenNar(narId); window.location.reload(); }}
              style={btnStyle('#DBEAFE', '#1E40AF', '1px solid #BFDBFE')}>
              Reopen
            </button>
          )}
          <button onClick={() => navigate('/nar/assessments')}
            style={btnStyle('white', '#6B7280', '1px solid #D1D5DB')}>
            ← Back
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '10px 20px', marginBottom: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0 }}>Filter:</span>
        <FilterPill label="Unanswered" active={filterUnanswered} onClick={() => setFilterUnanswered(v => !v)} />
        <FilterPill label="Remote" active={filterRemote} onClick={() => setFilterRemote(v => !v)} icon="🖥" />
        <FilterPill label="Met"           active={filterStatuses.has('met')}           color="#16a34a" onClick={() => toggleStatus('met')} />
        <FilterPill label="Partially Met" active={filterStatuses.has('partially_met')} color="#ca8a04" onClick={() => toggleStatus('partially_met')} />
        <FilterPill label="Not Met"       active={filterStatuses.has('not_met')}       color="#dc2626" onClick={() => toggleStatus('not_met')} />
        <FilterPill label="N/A"           active={filterStatuses.has('na')}            color="#6B7280" onClick={() => toggleStatus('na')} />
        {allTags.length > 0 && <span style={{ width: '1px', height: '18px', backgroundColor: '#E5E7EB', flexShrink: 0 }} />}
        {allTags.map(t => (
          <FilterPill key={t.id} label={t.name} active={filterTags.has(t.id)} color={t.color} onClick={() => toggleTag(t.id)} />
        ))}
        {anyFilter && (
          <button onClick={clearFilters}
            style={{ marginLeft: 'auto', padding: '4px 12px', fontSize: '12px', color: '#6B7280', background: 'none', border: '1px solid #D1D5DB', borderRadius: '12px', cursor: 'pointer' }}>
            Clear filters
          </button>
        )}
        {allTags.length === 0 && !anyFilter && (
          <span style={{ fontSize: '12px', color: '#D1D5DB' }}>No tag filters available</span>
        )}
      </div>

      {/* Body: sidebar + questions */}
      <div style={{ display: 'flex', gap: '16px', flex: 1, minHeight: 0 }}>

        {/* Sidebar — sections */}
        <div style={{ width: '220px', flexShrink: 0, backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', overflow: 'auto' }}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${PLM}`, fontSize: '10px', fontWeight: '700', color: PD, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Sections
          </div>
          {sections.map((sec, idx) => {
            const { total, answered } = sectionProgress(sec);
            const done   = total > 0 && answered === total;
            const active = !anyFilter && activeSec === idx;
            return (
              <button key={sec.id} onClick={() => { clearFilters(); setActiveSec(idx); }}
                style={{ width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', borderBottom: `1px solid ${PL}`, cursor: 'pointer', backgroundColor: active ? PL : 'white', transition: 'background 0.15s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px' }}>
                  <span style={{ fontSize: '12px', fontWeight: active ? '600' : '500', color: active ? PD : '#374151', lineHeight: '1.4' }}>
                    {sec.name}
                  </span>
                  {done && <span style={{ fontSize: '13px', flexShrink: 0 }}>✅</span>}
                </div>
                <div style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ flex: 1, height: '4px', backgroundColor: PLM, borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ width: total ? `${answered / total * 100}%` : '0%', height: '100%', backgroundColor: done ? '#16a34a' : P, borderRadius: '2px' }} />
                  </div>
                  <span style={{ fontSize: '10px', color: '#6B7280', flexShrink: 0 }}>{answered}/{total}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Question panel */}
        <div style={{ flex: 1, backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', overflow: 'auto' }}>
          {viewSections.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#6B7280' }}>
              {anyFilter ? 'No questions match the active filters.' : 'Select a section'}
            </div>
          ) : (
            <div>
              {anyFilter && (
                <div style={{ padding: '8px 20px', backgroundColor: PLM, borderBottom: `1px solid ${P}30`, fontSize: '12px', color: PD, fontWeight: '600' }}>
                  Showing filtered results across all sections
                </div>
              )}

              {viewSections.map(sec => (
                <div key={sec.id}>
                  {/* Section header */}
                  {anyFilter ? (
                    <div style={{ padding: '10px 20px', backgroundColor: PD, color: 'white', fontWeight: '700', fontSize: '13px' }}>
                      {sec.name}
                    </div>
                  ) : (
                    <div style={{ padding: '14px 20px', borderBottom: `2px solid ${PLM}`, backgroundColor: PL }}>
                      <h2 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: PD }}>{sec.name}</h2>
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6B7280' }}>
                        Weight: {sec.weight} &nbsp;·&nbsp; {(sec.categories || []).length} categories
                      </p>
                    </div>
                  )}

                  {/* Categories + questions */}
                  {(sec.categories || []).map(cat => (
                    <div key={cat.id}>
                      <div style={{ padding: '8px 20px', backgroundColor: '#F9FAFB', borderBottom: '1px solid #F3F4F6', borderTop: `2px solid ${PLM}` }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: PD }}>{cat.name}</span>
                        <span style={{ marginLeft: '10px', fontSize: '11px', color: '#9CA3AF' }}>weight {cat.weight}</span>
                      </div>
                      {(cat.questions || []).map((q, qi) => (
                        <QuestionRow
                          key={q.id}
                          q={q}
                          ans={answers[q.id] || {}}
                          qi={qi}
                          locked={isLocked}
                          isAdmin={isAdmin}
                          onChange={(field, val) => handleAnswer(q.id, field, val)}
                        />
                      ))}
                    </div>
                  ))}

                  {/* Prev / Next nav — only in single-section view */}
                  {!anyFilter && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 20px', borderTop: `1px solid ${PLM}`, marginTop: '8px' }}>
                      <button disabled={activeSec === 0} onClick={() => setActiveSec(s => s - 1)}
                        style={{ padding: '8px 16px', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '6px', fontWeight: '600', fontSize: '13px', cursor: activeSec === 0 ? 'not-allowed' : 'pointer', opacity: activeSec === 0 ? 0.4 : 1 }}>
                        ← Previous
                      </button>
                      <button disabled={activeSec >= sections.length - 1} onClick={() => setActiveSec(s => s + 1)}
                        style={{ padding: '8px 16px', backgroundColor: P, color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', fontSize: '13px', cursor: activeSec >= sections.length - 1 ? 'not-allowed' : 'pointer', opacity: activeSec >= sections.length - 1 ? 0.4 : 1 }}>
                        Next →
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Filter pill ───────────────────────────────────────────────────────────────

function FilterPill({ label, active, onClick, color, icon }) {
  const bg          = active ? (color ? color + '28' : PLM) : 'white';
  const textColor   = active ? (color || PD) : '#6B7280';
  const borderColor = active ? (color || P) : '#E5E7EB';
  return (
    <button onClick={onClick}
      style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: active ? '700' : '500', cursor: 'pointer', border: `1.5px solid ${borderColor}`, backgroundColor: bg, color: textColor, display: 'flex', alignItems: 'center', gap: '4px', transition: 'all 0.15s' }}>
      {icon && <span style={{ fontSize: '11px' }}>{icon}</span>}
      {label}
    </button>
  );
}

// ── Question row ─────────────────────────────────────────────────────────────

function QuestionRow({ q, ans, qi, locked, isAdmin, onChange }) {
  const [showComments, setShowComments] = useState(!!(ans.comments || ans.vcio_comments || ans.internal_notes));
  const answered = !!ans.answer;

  return (
    <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', backgroundColor: answered ? 'white' : '#FFFBEB' }}>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        {/* Number bubble */}
        <div style={{ flexShrink: 0, width: '22px', height: '22px', borderRadius: '50%', backgroundColor: answered ? '#D1FAE5' : PLM, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: answered ? '#065F46' : PD, marginTop: '2px' }}>
          {answered ? '✓' : qi + 1}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14px', color: '#111827', lineHeight: '1.5', marginBottom: '10px' }}>
            {q.question_text}
            {q.weight !== 3 && (
              <span style={{ marginLeft: '8px', fontSize: '11px', color: '#9CA3AF' }}>weight {q.weight}</span>
            )}
            {q.answerable_remotely ? (
              <span style={{ marginLeft: '8px', fontSize: '11px', backgroundColor: '#DBEAFE', color: '#1E40AF', padding: '1px 6px', borderRadius: '8px' }}>Remote OK</span>
            ) : null}
            {(q.tags || []).map(t => (
              <span key={t.id} style={{ marginLeft: '6px', fontSize: '11px', padding: '1px 6px', borderRadius: '8px', backgroundColor: (t.color || '#6B3FA0') + '22', color: t.color || P, border: `1px solid ${(t.color || '#6B3FA0')}44` }}>
                {t.name}
              </span>
            ))}
          </div>
          {q.micro_help && (
            <div style={{ fontSize: '12px', color: '#6B7280', backgroundColor: '#F9FAFB', padding: '6px 10px', borderRadius: '4px', marginBottom: '10px', fontStyle: 'italic' }}>
              {q.micro_help}
            </div>
          )}

          {/* Answer buttons */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {Object.entries(ANSWER_META).map(([val, meta]) => {
              if (val === 'na' && !q.allow_na) return null;
              const selected = ans.answer === val;
              return (
                <button key={val} disabled={locked}
                  onClick={() => !locked && onChange('answer', selected ? null : val)}
                  style={{ padding: '6px 14px', backgroundColor: selected ? meta.bg : 'white', color: selected ? meta.color : '#6B7280', border: `1.5px solid ${selected ? meta.border : '#E5E7EB'}`, borderRadius: '20px', fontSize: '13px', fontWeight: selected ? '700' : '500', cursor: locked ? 'not-allowed' : 'pointer', transition: 'all 0.15s' }}>
                  {meta.label}
                </button>
              );
            })}
            <button onClick={() => setShowComments(s => !s)}
              style={{ padding: '6px 12px', backgroundColor: 'white', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: '20px', fontSize: '12px', cursor: 'pointer' }}>
              {showComments ? 'Hide notes' : '+ Notes'}
            </button>
          </div>

          {/* Comment fields */}
          {showComments && (
            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <textarea value={ans.comments || ''} onChange={e => onChange('comments', e.target.value)}
                disabled={locked} placeholder="Comments (visible in report)…" rows={2}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              {isAdmin && (
                <>
                  <textarea value={ans.vcio_comments || ''} onChange={e => onChange('vcio_comments', e.target.value)}
                    disabled={locked} placeholder="vCIO comments…" rows={2}
                    style={{ width: '100%', padding: '8px 10px', border: `1px solid ${PLM}`, borderRadius: '6px', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', backgroundColor: '#FDFBFF' }} />
                  <textarea value={ans.internal_notes || ''} onChange={e => onChange('internal_notes', e.target.value)}
                    disabled={locked} placeholder="Internal notes (not in report)…" rows={2}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', backgroundColor: '#F9FAFB' }} />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
